// ── Types ────────────────────────────────────────────────────────────────────
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import {S} from "@/pages/mindmap/CarNodes/SummaryNode/ReportStyle.ts";
import {useRef, useState} from "react";
import FileIcon from "@/assets/icons/FileIcon.tsx";
import {TreeNode} from "@/types";
import {get_color, render_line} from "@/pages/mindmap/CarNodes/BasicCardNode.tsx";
import {formatCompactNumber, formatPercentage, getMonthAbbr} from "@/utils/format.ts";
import {useAppStore} from "@/store/appStore.ts";
import {COLOR_MAP} from "@/pages/mindmap/constant.ts";
// @ts-ignore
import { exportToPptx } from 'dom-to-pptx';

const PPT_SLIDE_WIDTH = 1560;
const PPT_SLIDE_HEIGHT = 810;


interface ReportModelProps {
    reportData: Record<string, number>;
    loading: boolean;
    onOpenChange: (open: boolean) => void;
    onDownload: () => Promise<void>;
    root_node: TreeNode;
}

// ── Component ────────────────────────────────────────────────────────────────

function ReportButton({borderColor}: { borderColor: string }) {
    const [hovered, setHovered] = useState(false);
    const shadowColor = borderColor === '#dc2626' ? 'rgba(220, 38, 38,' : 'rgba(30, 41, 59,';
    return (
        <AlertDialog.Trigger asChild>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: 'flex',
                    padding: '3px 10px',
                    background: 'white',
                    border: `1.5px solid ${borderColor}`,
                    borderRadius: 20,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: hovered
                        ? `0 4px 12px ${shadowColor} 0.25)`
                        : `0 1px 4px ${shadowColor} 0.12)`,
                    transform: hovered ? 'translateY(-1px)' : 'none',
                }}
            >
                <FileIcon/>
                <span style={{
                    marginLeft: '4px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: borderColor,
                    lineHeight: 1,
                    whiteSpace: 'nowrap'
                }}>Report</span>
            </div>
        </AlertDialog.Trigger>
    );
}



const ReportModel: React.FC<ReportModelProps> = (
    {
        reportData,
        loading,
        onOpenChange,
        onDownload,
        root_node
    }) => {
    const status = 'good'
    const datas = root_node.metrics_map || {};
    const exportingRef = useRef(false);
    const [exporting, setExporting] = useState(false);
    const selectedBatchMonth = useAppStore((state) => state.selectBatch?.month || '');

    const export_ppt = async ()=>{
        const source = document.getElementById("slide-content");
        if (!source || exportingRef.current) {
            return;
        }
        exportingRef.current = true;

        const exportHost = document.createElement("div");
        const exportSlide = source.cloneNode(true) as HTMLDivElement;

        Object.assign(exportHost.style, {
            position: "fixed",
            left: "-100000px",
            top: "0",
            width: `${PPT_SLIDE_WIDTH}px`,
            height: `${PPT_SLIDE_HEIGHT}px`,
            overflow: "hidden",
            pointerEvents: "none",
        });
        exportHost.setAttribute("aria-hidden", "true");

        Object.assign(exportSlide.style, {
            width: `${PPT_SLIDE_WIDTH}px`,
            height: `${PPT_SLIDE_HEIGHT}px`,
            minWidth: `${PPT_SLIDE_WIDTH}px`,
            minHeight: `${PPT_SLIDE_HEIGHT}px`,
            maxWidth: "none",
            maxHeight: "none",
            boxSizing: "border-box",
            overflow: "hidden",
            padding: "20px 24px",
            margin: "0",
        });

        exportHost.appendChild(exportSlide);
        document.body.appendChild(exportHost);
        setExporting(true);

        try {
            await document.fonts?.ready;
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            await exportToPptx(exportSlide, {
                fileName: `YTD PERFORMANCE.pptx`,
                // width: 10,
                // height: 5.625,
            });
        } finally {
            exportHost.remove();
            exportingRef.current = false;
            setExporting(false);
        }
    }


    const render_content = () => {
        return <>
            <div style={S.header}>
                <h1 style={{...S.mainTitle, width:'35%'}}>Commentary</h1>
                <span
                    style={S.displayDate}>{getMonthAbbr(selectedBatchMonth)} YTD PERFORMANCE</span>
            </div>

            <div style={S.columns}>
                {/* Left column */}
                <div style={{display: "flex", flexDirection: "column", gap: 30}}>
                    <div key={`section-l-SURGERY`}>
                        <span style={{...S.sectionTitle, width:'35%'}}>1. Surgery Performance</span>
                        <div style={{color: 'rgb(30, 41, 59)'}}>
                            <div style={{...COLOR_MAP['1'], width:'100%'}}>IMS:</div>
                            <div style={{marginLeft: '10px', lineHeight: '1.5'}}>
                                {render_line('ims', 'ytd', datas)}
                                {render_line('ims', 'mtd', datas)}
                            </div>
                            <div style={{...COLOR_MAP['1'], width:'100%'}}>NTS:</div>
                            <div style={{marginLeft: '10px'}}>
                                {render_line('nts', 'ytd', datas)}
                                {render_line('nts', 'mtd', datas)}
                            </div>
                            <div style={{...COLOR_MAP['1'], width:'100%'}}>VBP/Non-VBP:</div>
                            <div style={{marginLeft: '10px'}}>
                                {render_line('ims_vbp', 'ytd', datas)}
                                {render_line('ims_nonvbp', 'ytd', datas)}
                            </div>
                        </div>
                    </div>
                    <div key={`section-l-Growth`}>
                        <span style={S.sectionTitle}>2. Core Growth Plan (YTD)</span>
                        <ul style={S.list}>
                            <li>ENDO - # {reportData['growth_plan_hosp_cnt_bu_endo_growth']} target HSPs:
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_endo_growth'])}
                            </li>
                            <li>WCH - # {reportData['growth_plan_hosp_cnt_bu_wch_growth']} target HSPs:
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_wch_growth'])}
                            </li>
                            <li>BIO - # {reportData['growth_plan_hosp_cnt_bu_bio_growth']} target HSPs:
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_bio_growth'])}
                            </li>
                        </ul>
                    </div>

                    <div key={`section-l-BROAD`}>
                        <span style={S.sectionTitle}>3. Broad Market (YTD)</span>
                        <ul style={S.list}>
                            <li>
                                IMS Achi {formatPercentage(reportData['ims_amt_ytd_achi_bu_bm'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_bm'])}
                            </li>
                            <li>
                                Vol Gro% BM VS Core in
                                ENDO
                                ({formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_endo_bm'])} vs {formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_endocore'])}),
                                WCH
                                ({formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_wch_bm'])} vs {formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_wchcore'])}),
                                BIO
                                ({formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_bio_bm'])} vs {formatPercentage(reportData['ims_amt_ytd_vol_growth_bu_biocore'])})
                            </li>
                            <li>
                                BM - # {reportData['growth_plan_hosp_cnt_bu_bm_growth']} target HSPs in Accelerate Plan:
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_bm_growth'])}
                            </li>
                            <li>
                                BM - # {reportData['bm_ka_hosp_cnt_bu_bm_bm_ka']} target HSPs in KA Plan:
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_bu_bm_bm_ka'])}
                            </li>
                        </ul>
                    </div>


                </div>
                {/* Right column */}
                <div style={{display: "flex", flexDirection: "column", gap: 20, maxWidth: 500}}>

                    <div key={`section-r`}>
                        <span style={S.sectionTitle}>4. Portfolio Innovation (YTD)</span>
                        <br/>
                        <br/>
                        <div style={{color: "#cc0000", fontWeight: 700, fontSize: 17}}>
                            ◆ Strategic Products
                        </div>
                        <ul style={S.list}>
                            <li>
                                HD1000i & HAR1100 (AE): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_hd1000i_&_har1100_ae'])},
                                Vol
                                Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_hd1000i_&_har1100_ae'])}
                            </li>
                            <li>
                                SFX (WCH): IMS Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_sfx_wch'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_sfx_wch'])}
                            </li>
                            <li>
                                Surgiflo (BIO): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_surgiflo_bio'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_surgiflo_bio'])}
                            </li>
                            <li>
                                Advanced SURGICEL(M/L) (BIO): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_advanced_surgicel_(m/l)_bio'])},
                                Vol
                                Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_advanced_surgicel_(m/l)_bio'])}
                            </li>
                        </ul>

                        <br/>
                        <div style={{color: "#cc0000", fontWeight: 700, fontSize: 17}}>
                            ◆ Local Portfolio
                        </div>
                        <ul>
                            <li>
                                Leap(ACE+7)(AE): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_leap(ace+7)_ae'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_leap(ace+7)_ae'])}
                            </li>
                            <li>
                                Safe(VCP)(WCH): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_safe(vcp)_wch'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_safe(vcp)_wch'])}
                            </li>
                        </ul>

                        <br/>
                        <div style={{color: "#cc0000", fontWeight: 700, fontSize: 17}}>
                            ◆ NPI
                        </div>
                        <ul>
                            <li>
                                E4000(GUN)(MIP): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_e4000_(gun)_mip'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_e4000_(gun)_mip'])}
                            </li>
                            <li>
                                Aggregate(BIO): IMS
                                Achi {formatPercentage(reportData['ims_amt_ytd_achi_product_aggregate_bio'])},
                                Vol Gro {formatPercentage(reportData['ims_ytd_vol_growth_product_aggregate_bio'])}
                            </li>
                        </ul>

                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={S.footer}>
                <span style={{fontWeight: 700, color: "#cc0000", fontSize: 22,width:'50%'}}>
                  J&amp;J MedTech
                </span>
                <span style={{color: "#cc0000", fontWeight: 700, fontSize: 22,width:'10%'}}>
                    Surgery
                 </span>
            </div>
        </>
    }

    return (
        <AlertDialog.Root onOpenChange={onOpenChange}>
            <ReportButton
                borderColor={status === 'good' ? '#1e293b' : '#dc2626'}
            />
            <AlertDialog.Portal>
                <AlertDialog.Overlay style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1000,
                }}/>
                <AlertDialog.Content
                    // ref={slideRef}
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90vw",
                        maxWidth: 1680,
                        maxHeight: "min(92vh, 860px)",
                        backgroundColor: "white",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        overflow: "auto",
                        display: "flex",
                        flexDirection: "column",
                        borderLeft: "6px solid #1e293b",
                        zIndex: 1001,
                    }}
                >
                    {/* Toolbar */}
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "10px 18px",
                        backgroundColor: "#f1f5f9",
                        borderBottom: "1px solid #e2e8f0",
                        gap: 10,
                    }}>
                        <button
                            onClick={export_ppt}
                            disabled={exporting}
                            style={{
                                padding: "6px 12px",
                                backgroundColor: "#cc0000",
                                color: "white",
                                border: "none",
                                borderRadius: 2,
                                cursor: exporting ? "wait" : "pointer",
                                opacity: exporting ? 0.65 : 1,
                                fontSize: 13,
                                fontWeight: 600,
                            }}
                        >
                            Download PPT
                        </button>
                        <AlertDialog.Cancel asChild>
                            <button style={{
                                padding: "6px 10px",
                                backgroundColor: "white",
                                color: "#64748b",
                                border: "1px solid #e2e8f0",
                                borderRadius: 2,
                                cursor: "pointer",
                                fontSize: 14,
                                fontWeight: 600,
                            }}>
                                ×
                            </button>
                        </AlertDialog.Cancel>
                    </div>

                    {/* Content area */}
                    <div
                        className="slide-content"
                        id="slide-content"
                        style={{
                        flex: 1,
                        overflow: "auto",
                        padding: "20px 24px",
                        backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                    }}>
                        <div style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily: "Arial",
                        }}>
                            {render_content()}
                        </div>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export default ReportModel;
